import {
  Table,
  tableCellClasses,
  TableBody,
  TableRow,
  TableCell,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement } from "react";

import { InternalLink } from "~frontend/components/Link";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { formatDate } from "~frontend/util/dateFormat";

import { formatMillicentsAsDollars } from "../pricing/priceFormatting";
import { formatMaybeNegativeMillicentsAsDollarsWithDollarSign } from "./util";

export function BudgetDetails({
  subaccountSlug,
  startDate,
  endDate,
  allocationInMillicents,
  usageInMillicents,
}: {
  subaccountSlug: string;
  startDate: number;
  endDate?: number;
  allocationInMillicents: number;
  usageInMillicents: number;
}): ReactElement {
  const tenant = useTenant()!;

  const formatDateForQueryParam = (millis: number) =>
    DateTime.fromMillis(millis).toISODate();

  const usagePageLink = `${
    tenant.subaccountsEnabled
      ? `/subaccounts/${subaccountSlug}/usage`
      : `/admin/usage`
  }?start_date=${formatDateForQueryParam(startDate)}&end_date=${formatDateForQueryParam(endDate ? endDate - 1000 * 60 * 60 * 24 : DateTime.now().toMillis())}`;

  return (
    <Table
      sx={{
        [`& .${tableCellClasses.root}`]: {
          borderBottom: "none",
          px: 0,
          pt: 0,
          pb: "8px",

          "&:last-child": {
            textAlign: "right",
          },
        },
      }}
    >
      <TableBody>
        <TableRow>
          <TableCell>
            <Typography variant="body2Bold" sx={{ textAlign: "right" }}>
              Budget, {formatDate(startDate)} to{" "}
              {endDate ? formatDate(endDate) : "Present"}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2Bold">
              ${formatMillicentsAsDollars(allocationInMillicents)}
            </Typography>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Typography variant="body2Bold">
              Spend, {formatDate(startDate)} to{" "}
              {endDate ? formatDate(endDate) : "Present"}
            </Typography>{" "}
            <Typography variant="body3">
              <InternalLink to={usagePageLink} target="_blank">
                View Usage
              </InternalLink>
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2Bold">
              ${formatMillicentsAsDollars(usageInMillicents)}
            </Typography>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Typography variant="body2Bold">Remaining Balance</Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2Bold">
              {formatMaybeNegativeMillicentsAsDollarsWithDollarSign(
                allocationInMillicents - usageInMillicents
              )}
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
